import type { Rect, RegionAnchor } from "../format/types.ts";

/** Resize one PDF-space corner, keeping its opposite corner and every other rect fixed. */
export function resizeRegion(anchor: RegionAnchor, rectIndex: number, xSide: 0 | 2, ySide: 1 | 3, dx: number, dy: number, bounds: Rect): RegionAnchor {
  const rect = anchor.rects[rectIndex];
  if (!rect || (!dx && !dy) || !Number.isFinite(dx) || !Number.isFinite(dy)) return anchor;
  const fixedX = rect[xSide === 0 ? 2 : 0];
  const fixedY = rect[ySide === 1 ? 3 : 1];
  // Imported geometry may be outside the page. Never repair it by moving the fixed corner.
  if (fixedX < bounds[0] || fixedX > bounds[2] || fixedY < bounds[1] || fixedY > bounds[3]) return anchor;
  const minX = Math.ceil((xSide === 0 ? bounds[0] : Math.max(bounds[0], fixedX + 1)) * 100);
  const maxX = Math.floor((xSide === 0 ? Math.min(bounds[2], fixedX - 1) : bounds[2]) * 100);
  const minY = Math.ceil((ySide === 1 ? bounds[1] : Math.max(bounds[1], fixedY + 1)) * 100);
  const maxY = Math.floor((ySide === 1 ? Math.min(bounds[3], fixedY - 1) : bounds[3]) * 100);
  // No rounded position can satisfy both bounds and minimum size without moving the fixed corner.
  if (minX > maxX || minY > maxY) return anchor;
  const x = Math.max(minX, Math.min(maxX, Math.round((rect[xSide] + dx) * 100))) / 100;
  const y = Math.max(minY, Math.min(maxY, Math.round((rect[ySide] + dy) * 100))) / 100;
  if (x === rect[xSide] && y === rect[ySide]) return anchor;
  const resized: Rect = [...rect];
  resized[xSide] = x;
  resized[ySide] = y;
  const rects = anchor.rects.slice();
  rects[rectIndex] = resized;
  return { ...anchor, rects };
}
